import {
  SidebarList,
  SidebarListItemProps,
} from "@/lib/Components/SideBarList/SideBarList";
import { formatDateTimeSinceNow } from "@/lib/Formatters/formatDateTimeSinceNow";
import { MailIcon } from "lucide-react";
import { useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { useInfiniteGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { getSupplierListQueryVariables } from "@/app/Suppliers/Utils/getSupplierListQueryVariables";
import {
  SupplierListItem,
  supplierListQuery,
} from "@/app/Suppliers/GraphQL/supplierListQuery";
import { Highlighter } from "@/lib/Components/Highlighter";
import { SupplierIcon } from "@/app/Suppliers/Components/SupplierIcon";
import { keepPreviousData } from "@tanstack/react-query";

export function SupplierListScreen() {
  const searchParams = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/suppliers/_supplierRecordLayout",
  });
  const [search, setSearch] = useState("");

  const { data, fetchNextPage, isFetching, isFetchingNextPage } =
    useInfiniteGqlQuery(
      supplierListQuery,
      getSupplierListQueryVariables(searchParams, search),
      {
        getNextPageParam: (lastPage) => {
          const paginatorInfo = lastPage.suppliers.paginatorInfo;
          if (paginatorInfo.hasMorePages) {
            return {
              page: paginatorInfo.currentPage + 1,
            };
          }

          return undefined;
        },
        initialPageParam: {
          page: 1,
        },
        placeholderData: keepPreviousData,
      },
    );

  const items = data?.pages.flatMap((i) => i.suppliers.data);
  const total = data?.pages.at(0)?.suppliers.paginatorInfo.total ?? 0;

  return (
    <SidebarList<SupplierListItem>
      onClearFilters={() => {
        setSearch("");
      }}
      items={items}
      listRoute={"/$tenant/$orgId/supplier/{-$supplierId}/suppliers"}
      search={search}
      onSearchChange={setSearch}
      fetchNextPage={fetchNextPage}
      isFetching={isFetching}
      isFetchingNextPage={isFetchingNextPage}
      totalCount={total}
      itemNode={(i) => <SupplierItem model={i} search={search} />}
      linkProps={(i) => ({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/suppliers/$id",
        params: (prev) => ({
          ...prev,
          id: i.id,
        }),
      })}
    />
  );
}

function SupplierItem({
  model,
  search,
}: SidebarListItemProps<SupplierListItem> & { search: string }) {
  return (
    <div className="flex justify-between space-x-2">
      <div className="space-y-1.5 truncate">
        <p className="truncate text-sm font-semibold text-gray-900 group-hover:text-blue-700">
          <Highlighter searchString={search}>{model.name}</Highlighter>
        </p>
        {model.email && (
          <p className="flex items-center truncate text-xs text-gray-600">
            <MailIcon className="mr-1.5 inline h-3.5 w-3.5 shrink-0 text-gray-500" />
            {model.email}
          </p>
        )}
        <p>
          <time
            dateTime={model.created_at}
            className="text-xs italic text-gray-500"
          >
            {formatDateTimeSinceNow(model.created_at)}
          </time>
        </p>
      </div>

      <div className="mt-1 min-w-0 shrink-0 self-start">
        <SupplierIcon model={model} className="size-10 rounded-md" />
      </div>
    </div>
  );
}
